<template>
  <component
    :is="tag"
    v-bind="buttonAttrs"
    v-on="$listeners"
    :type="resolvedType"
    :class="rootClass"
    :style="{ borderRadius: radius }"
  >
    <slot />
  </component>
</template>

<script>
export default {
  name: "CTailwindBtn",
  inheritAttrs: false,
  props: {
    radius: {
      type: String,
      default: "8px",
    },
    tag: {
      type: String,
      default: "button",
    },
    type: {
      type: String,
      default: "button",
    },
    variant: {
      type: String,
      default: "default",
    },
  },
  computed: {
    buttonAttrs() {
      return {
        ...this.$attrs,
      };
    },
    resolvedType() {
      return this.tag === "button" ? this.type : undefined;
    },
    rootClass() {
      const v = ["outline", "ghost", "primary"].includes(this.variant) ? this.variant : "default";
      return ["ctw-btn", `ctw-btn--${v}`];
    },
  },
};
</script>

<style scoped>
/*
  Vuetify reset uses unlayered `button { background: transparent; border: none }` which wins over
  Tailwind utilities inside @layer. These rules are unlayered + higher specificity than `button`.
*/
.ctw-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding-inline: calc(var(--spacing) * 3);
  padding-block: calc(var(--spacing) * 2);
  font-family: var(--font-inter), var(--font-sans);
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  white-space: nowrap;
  border-style: solid;
  border-width: 1px;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition-property: color, background-color, border-color, box-shadow, opacity;
  transition-duration: var(--default-transition-duration);
  transition-timing-function: var(--default-transition-timing-function);
}

.ctw-btn:focus {
  outline: none;
}

.ctw-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* default — same intent as `btn bg-white … border-gray-200 …` in new_design */
.ctw-btn--default {
  background-color: var(--color-white);
  border-color: var(--color-gray-200);
  color: var(--color-gray-800);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.ctw-btn--default:hover:not(:disabled) {
  border-color: var(--color-gray-300);
}

html.dark .ctw-btn--default {
  background-color: var(--color-gray-800);
  border-color: color-mix(in srgb, var(--color-gray-700) 60%, transparent);
  color: var(--color-gray-300);
}

html.dark .ctw-btn--default:hover:not(:disabled) {
  border-color: var(--color-gray-600);
}

.ctw-btn--outline {
  background-color: transparent;
  border-color: var(--color-gray-300);
  color: var(--color-gray-800);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.ctw-btn--outline:hover:not(:disabled) {
  background-color: var(--color-gray-50);
}

html.dark .ctw-btn--outline {
  border-color: var(--color-gray-600);
  color: var(--color-gray-300);
}

html.dark .ctw-btn--outline:hover:not(:disabled) {
  background-color: var(--color-gray-800);
}

.ctw-btn--ghost {
  background-color: transparent;
  border-color: transparent;
  color: var(--color-gray-800);
  box-shadow: none;
}

.ctw-btn--ghost:hover:not(:disabled) {
  background-color: var(--color-gray-100);
}

html.dark .ctw-btn--ghost {
  color: var(--color-gray-300);
}

html.dark .ctw-btn--ghost:hover:not(:disabled) {
  background-color: var(--color-gray-800);
}

/* Vuetify theme primary — same token as stat bars / links */
.ctw-btn--primary {
  background-color: var(--v-primary-base);
  border-color: color-mix(in srgb, var(--v-primary-base) 82%, black);
  color: #fff;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.12);
}

.ctw-btn--primary:hover:not(:disabled) {
  background-color: color-mix(in srgb, var(--v-primary-base) 90%, black);
  border-color: color-mix(in srgb, var(--v-primary-base) 78%, black);
}

html.dark .ctw-btn--primary {
  background-color: var(--v-primary-base);
  border-color: color-mix(in srgb, var(--v-primary-base) 70%, black);
  color: #fff;
}

html.dark .ctw-btn--primary:hover:not(:disabled) {
  background-color: color-mix(in srgb, var(--v-primary-base) 82%, white);
  border-color: color-mix(in srgb, var(--v-primary-base) 65%, black);
}
</style>
